import classNames from 'classnames'
import DOMPurify from 'dompurify'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  SegmentationCarouselWithImageCard,
  SegmentationCarouselWithImageProps,
  SegmentationCarouselWithImageTheme,
} from 'src/types/segmentationCarouselWithImage'
import { getResponsiveVisibility } from 'src/utils/getResponsiveVisibility'

import SegmentationImageCard from './SegmentationImageCard'

const MAX_ITEMS = 12
const MIN_ITEMS_FOR_DESKTOP_SCROLL = 3

const isValidCard = (card?: SegmentationCarouselWithImageCard) => {
  return Boolean(
    card?.imageProperties?.image?.trim() && card?.textPrimary?.trim()
  )
}

export default function SegmentationCarouselWithImage({
  enableSession = true,
  showOnDesktop = true,
  showOnMobile = true,
  theme = 'light',
  title,
  cards = [],
}: SegmentationCarouselWithImageProps) {
  const listRef = useRef<HTMLUListElement>(null)
  const [hasScrollableContent, setHasScrollableContent] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const normalizedTheme: SegmentationCarouselWithImageTheme =
    theme === 'dark' ? 'dark' : 'light'

  const validCards = useMemo(
    () => cards.filter((card) => isValidCard(card)).slice(0, MAX_ITEMS),
    [cards]
  )

  const visibilityClasses = useMemo(
    () => getResponsiveVisibility(showOnDesktop, showOnMobile),
    [showOnDesktop, showOnMobile]
  )

  const shouldUseDesktopScroll =
    validCards.length > MIN_ITEMS_FOR_DESKTOP_SCROLL

  const safeTitle = useMemo(() => {
    if (typeof title !== 'string' || title.trim().length === 0) {
      return ''
    }

    return typeof DOMPurify?.sanitize === 'function'
      ? DOMPurify.sanitize(title)
      : title
  }, [title])

  const updateDesktopScrollState = useCallback(() => {
    if (!shouldUseDesktopScroll || !listRef.current) {
      setHasScrollableContent(false)
      setCanScrollLeft(false)
      setCanScrollRight(false)

      return
    }

    const listElement = listRef.current
    const maxScrollLeft = listElement.scrollWidth - listElement.clientWidth
    const hasOverflow = maxScrollLeft > 4

    setHasScrollableContent(hasOverflow)

    setCanScrollLeft(hasOverflow && listElement.scrollLeft > 4)
    setCanScrollRight(hasOverflow && listElement.scrollLeft < maxScrollLeft - 4)
  }, [shouldUseDesktopScroll])

  useEffect(() => {
    updateDesktopScrollState()

    const listElement = listRef.current

    if (!listElement || !shouldUseDesktopScroll) {
      return
    }

    const handleResize = () => {
      updateDesktopScrollState()
    }

    listElement.addEventListener('scroll', updateDesktopScrollState)
    window.addEventListener('resize', handleResize)

    return () => {
      listElement.removeEventListener('scroll', updateDesktopScrollState)
      window.removeEventListener('resize', handleResize)
    }
  }, [shouldUseDesktopScroll, updateDesktopScrollState])

  if (!enableSession || validCards.length === 0) {
    return null
  }

  const themeSectionClass =
    normalizedTheme === 'dark'
      ? 'bg-restructure-background-primary-inverted border-y border-white/10'
      : 'bg-restructure-background-primary'

  const themeTitleClass =
    normalizedTheme === 'dark' ? 'text-white' : 'text-fullDark'

  const prevButtonBaseClass =
    normalizedTheme === 'dark'
      ? 'border border-white/20 bg-black/20 text-white'
      : 'border border-restructure-border-secondary bg-restructure-background-primary text-restructure-primary'

  const nextButtonBaseClass =
    normalizedTheme === 'dark'
      ? 'border border-white/10 bg-restructure-background-primary text-restructure-primary'
      : 'border border-restructure-border-secondary bg-restructure-background-primary text-restructure-primary'

  return (
    <section
      data-testid="segmentation-carousel-with-image"
      className={classNames(
        'w-full py-8 md:py-14',
        themeSectionClass,
        visibilityClasses
      )}
      aria-label="Carrossel de segmentação com imagem"
    >
      <div className="mx-auto w-full max-w-[1440px] pl-5 md:pl-0">
        {safeTitle && (
          <h2
            data-testid="segmentation-carousel-with-image-title"
            className={classNames(
              'max-w-[330px] text-[35px] font-black italic leading-[39px] tracking-[-0.858px] uppercase md:max-w-[620px] md:text-[72px] md:leading-[66px] md:tracking-[-1.44px] text-left',
              themeTitleClass
            )}
            dangerouslySetInnerHTML={{ __html: safeTitle }}
          />
        )}

        <div className="relative mt-6 md:mt-10">
          {shouldUseDesktopScroll && hasScrollableContent && (
            <div className="absolute -top-[86px] right-[60px] z-[2] hidden items-center gap-2 md:flex">
              <button
                type="button"
                aria-label="Ver cards anteriores"
                onClick={() => {
                  listRef.current?.scrollBy({
                    left: -(listRef.current?.clientWidth ?? 0) * 0.85,
                    behavior: 'smooth',
                  })
                }}
                disabled={!canScrollLeft}
                className={classNames(
                  'flex h-[46px] w-[46px] items-center justify-center rounded-full transition-opacity',
                  prevButtonBaseClass,
                  canScrollLeft ? 'opacity-100' : 'opacity-55'
                )}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M11.25 4.5 6.75 9l4.5 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button
                type="button"
                aria-label="Ver próximos cards"
                onClick={() => {
                  listRef.current?.scrollBy({
                    left: (listRef.current?.clientWidth ?? 0) * 0.85,
                    behavior: 'smooth',
                  })
                }}
                disabled={!canScrollRight}
                className={classNames(
                  'flex h-[46px] w-[46px] items-center justify-center rounded-full transition-opacity',
                  nextButtonBaseClass,
                  canScrollRight ? 'opacity-100' : 'opacity-55'
                )}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M6.75 4.5 11.25 9l-4.5 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}

          <ul
            ref={listRef}
            className={classNames(
              'list-none gap-2 md:gap-[8px]',
              'grid grid-flow-col auto-cols-[334px] overflow-x-auto pb-2 pr-5 snap-x snap-mandatory no-scrollbar md:auto-cols-[346px]',
              shouldUseDesktopScroll
                ? 'md:grid-flow-col md:overflow-x-auto md:pr-0'
                : 'md:grid-flow-row md:auto-cols-auto md:grid-cols-[repeat(3,minmax(0,1fr))] md:overflow-visible md:pr-[60px]'
            )}
          >
            {validCards.map((card, index) => (
              <li
                key={`${card.textPrimary ?? ''}-${index}`}
                className={classNames(
                  'h-[436px] snap-start md:h-[464px] cursor-auto md:cursor-pointer',
                  shouldUseDesktopScroll
                    ? 'md:w-[346px] md:shrink-0'
                    : 'md:w-full'
                )}
              >
                <SegmentationImageCard card={card} theme={normalizedTheme} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
